(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const initTrain = (root) => {
    if (root.dataset.gm3TrainReady === 'true') return;
    root.dataset.gm3TrainReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-gm3-train-tab]'));
    const tabsNav = root.querySelector('[data-gm3-train-tabs-nav]');
    const tabsViewport = root.querySelector('[data-gm3-train-tabs-viewport]');
    const tabsTrack = root.querySelector('[data-gm3-train-tabs-track]');
    const tabsPrevBtn = root.querySelector('[data-gm3-train-tabs-prev]');
    const tabsNextBtn = root.querySelector('[data-gm3-train-tabs-next]');
    const mediaItems = Array.from(root.querySelectorAll('[data-gm3-train-media]'));
    const itemButtons = Array.from(root.querySelectorAll('[data-gm3-train-item]'));
    const trainButtons = Array.from(root.querySelectorAll('[data-gm3-train-button]'));
    const viewport = root.querySelector('[data-gm3-train-viewport]');
    const track = root.querySelector('[data-gm3-train-track]');
    const slides = Array.from(root.querySelectorAll('[data-gm3-train-slide]'));
    const dotsWrap = root.querySelector('[data-gm3-train-dots]');
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const autoplayEnabled = root.dataset.gm3TrainAutoplay === 'true';

    if (tabs.length === 0 || itemButtons.length === 0) return;

    let activeTab = tabs[0].dataset.gm3TrainTab;
    let activeIndex = Number.parseInt(itemButtons[0].dataset.gm3TrainIndex || '0', 10);
    let lastMediaIndex = activeIndex;
    let visibleSlides = [];
    let visibleSlideIndex = 0;
    let minX = 0;
    let currentX = 0;
    let resizeTimer = null;
    let tabsMinX = 0;
    let tabsCurrentX = 0;
    let tabsSuppressClick = false;
    const pausedIndexes = new Set();

    const drag = {
      pointerId: null,
      startX: 0,
      startTranslate: 0,
      moved: false,
    };

    const tabsDrag = {
      pointerId: null,
      startX: 0,
      startTranslate: 0,
      moved: false,
    };

    const getVideo = (element) => element?.querySelector('video');

    const getElementIndex = (element) => Number.parseInt(element?.dataset.gm3TrainIndex || '-1', 10);

    const getElementsByIndex = (index) =>
      [...mediaItems, ...slides].filter((element) => getElementIndex(element) === index);

    const setVideoButtonState = (button, playing) => {
      if (!button) return;
      const playIcon = button.querySelector('.gm3-train__play-icon--play');
      const pauseIcon = button.querySelector('.gm3-train__play-icon--pause');
      if (playIcon) playIcon.hidden = playing;
      if (pauseIcon) pauseIcon.hidden = !playing;
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      button.setAttribute('aria-label', playing ? button.dataset.labelPause || 'Pause video' : button.dataset.labelPlay || 'Play video');
    };

    const updateAllVideoButtons = () => {
      [...mediaItems, ...slides].forEach((element) => {
        const video = getVideo(element);
        const button = element.querySelector('[data-gm3-train-video-toggle]');
        setVideoButtonState(button, Boolean(video && !video.paused));
      });
    };

    const syncVideos = () => {
      [...mediaItems, ...slides].forEach((element) => {
        const video = getVideo(element);
        if (!video) {
          const button = element.querySelector('[data-gm3-train-video-toggle]');
          setVideoButtonState(button, false);
          return;
        }

        const isMobileSlide = element.hasAttribute('data-gm3-train-slide');
        const elementIndex = getElementIndex(element);
        const shouldPlay =
          autoplayEnabled &&
          !pausedIndexes.has(elementIndex) &&
          !element.hidden &&
          element.classList.contains('is-active') &&
          activeIndex !== null &&
          (isMobileSlide ? mediaQuery.matches : !mediaQuery.matches);
        if (shouldPlay) {
          video.muted = true;
          video.playsInline = true;
          video.play().then(updateAllVideoButtons).catch(updateAllVideoButtons);
        } else {
          video.pause();
          updateAllVideoButtons();
        }
      });
    };

    const setActiveMedia = () => {
      const currentMediaIndex = activeIndex === null ? lastMediaIndex : activeIndex;

      mediaItems.forEach((media) => {
        const isCurrent = Number.parseInt(media.dataset.gm3TrainIndex || '-1', 10) === currentMediaIndex;
        const inTab = media.dataset.gm3TrainTabName === activeTab;
        media.hidden = !inTab;
        media.classList.toggle('is-active', isCurrent && inTab);
      });

      itemButtons.forEach((item) => {
        const isCurrent = activeIndex !== null && Number.parseInt(item.dataset.gm3TrainIndex || '-1', 10) === activeIndex;
        const inTab = item.dataset.gm3TrainTabName === activeTab;
        item.hidden = !inTab;
        item.classList.toggle('is-active', isCurrent && inTab);
        item.setAttribute('aria-expanded', isCurrent && inTab ? 'true' : 'false');
      });

      trainButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.gm3TrainTabName === activeTab);
      });
    };

    const getFirstIndexForTab = (tabName) => {
      const first = itemButtons.find((item) => item.dataset.gm3TrainTabName === tabName);
      return Number.parseInt(first?.dataset.gm3TrainIndex || '0', 10);
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';

      visibleSlides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = `common-carousel-dot${index === visibleSlideIndex ? ' is-active' : ''}`;
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show training item ${index + 1}`);
        dot.addEventListener('click', () => setVisibleSlide(index, true));
        dotsWrap.appendChild(dot);
      });
    };

    const updateDots = () => {
      if (!dotsWrap) return;
      Array.from(dotsWrap.children).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === visibleSlideIndex);
      });
    };

    const clampX = (x) => Math.max(minX, Math.min(0, x));

    const clampTabsX = (x) => Math.max(tabsMinX, Math.min(0, x));

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const normalizeTabsWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * tabsViewport.clientWidth;
      return event.deltaX;
    };

    const updateTabsOverflowState = () => {
      if (!tabsNav) return;
      const hasOverflow = tabsMinX < 0;
      tabsNav.classList.toggle('is-overflowing', hasOverflow);
      tabsNav.classList.toggle('has-overflow-left', hasOverflow && tabsCurrentX < -1);
      tabsNav.classList.toggle('has-overflow-right', hasOverflow && tabsCurrentX > tabsMinX + 1);
    };

    const setTabsX = (x, animate = false) => {
      if (!tabsTrack) return;
      tabsCurrentX = clampTabsX(x);
      tabsTrack.style.transition = animate ? 'transform 320ms ease' : 'none';
      tabsTrack.style.transform = `translate3d(${tabsCurrentX}px, 0, 0)`;
      updateTabsOverflowState();
    };

    const refreshTabsBounds = () => {
      if (!tabsViewport || !tabsTrack) return;
      const overflow = tabsTrack.scrollWidth - tabsViewport.clientWidth;
      tabsMinX = overflow > 1 ? -overflow : 0;
      setTabsX(tabsMinX === 0 ? 0 : tabsCurrentX);
    };

    const scheduleTabsRefresh = () => {
      refreshTabsBounds();
      requestAnimationFrame(refreshTabsBounds);
      window.setTimeout(refreshTabsBounds, 80);
    };

    const setX = (x, animate = false) => {
      if (!track) return;
      currentX = clampX(x);
      track.style.transition = animate ? 'transform 320ms ease' : 'none';
      track.style.transform = `translate3d(${currentX}px, 0, 0)`;
    };

    const getSlideTargetX = (slide) => {
      if (!viewport || !track || !slide) return 0;
      return currentX + viewport.getBoundingClientRect().left - slide.getBoundingClientRect().left;
    };

    const getClosestVisibleSlideIndex = () => {
      if (visibleSlides.length === 0) return 0;

      let closestIndex = visibleSlideIndex;
      let closestDistance = Infinity;

      visibleSlides.forEach((slide, index) => {
        const distance = Math.abs(getSlideTargetX(slide) - currentX);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updateVisibleSlideState = () => {
      if (visibleSlides.length === 0) return;

      visibleSlideIndex = getClosestVisibleSlideIndex();
      slides.forEach((slide) => slide.classList.remove('is-active'));

      const slide = visibleSlides[visibleSlideIndex];
      slide.classList.add('is-active');
      activeIndex = Number.parseInt(slide.dataset.gm3TrainIndex || '0', 10);

      updateDots();
      setActiveMedia();
      syncVideos();
    };

    const refreshBounds = () => {
      if (!viewport || !track || !mediaQuery.matches) {
        setX(0);
        return;
      }

      const overflow = track.scrollWidth - viewport.clientWidth;
      minX = overflow > 1 ? -overflow : 0;
      setX(currentX);
    };

    const setVisibleSlide = (index, animate = true) => {
      if (visibleSlides.length === 0) return;
      visibleSlideIndex = Math.max(0, Math.min(visibleSlides.length - 1, index));

      slides.forEach((slide) => slide.classList.remove('is-active'));
      const slide = visibleSlides[visibleSlideIndex];
      slide.classList.add('is-active');
      activeIndex = Number.parseInt(slide.dataset.gm3TrainIndex || '0', 10);
      lastMediaIndex = activeIndex;

      updateDots();
      refreshBounds();
      setX(getSlideTargetX(slide), animate);
      setActiveMedia();
      syncVideos();
    };

    const refreshSlides = () => {
      visibleSlides = slides.filter((slide) => slide.dataset.gm3TrainTabName === activeTab);

      slides.forEach((slide) => {
        const inTab = slide.dataset.gm3TrainTabName === activeTab;
        slide.hidden = !inTab;
        slide.classList.remove('is-active');
      });

      visibleSlideIndex = Math.max(0, visibleSlides.findIndex((slide) =>
        Number.parseInt(slide.dataset.gm3TrainIndex || '-1', 10) === activeIndex
      ));
      if (visibleSlideIndex < 0) visibleSlideIndex = 0;
      buildDots();
      setVisibleSlide(visibleSlideIndex, false);
    };

    const setTab = (tabName) => {
      activeTab = tabName;
      activeIndex = getFirstIndexForTab(activeTab);
      lastMediaIndex = activeIndex;

      tabs.forEach((tab) => {
        const active = tab.dataset.gm3TrainTab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      setActiveMedia();
      refreshSlides();
      syncVideos();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setTab(tab.dataset.gm3TrainTab));
    });

    tabsPrevBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX + tabsViewport.clientWidth * 0.65, true);
    });

    tabsNextBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX - tabsViewport.clientWidth * 0.65, true);
    });

    tabsViewport?.addEventListener(
      'wheel',
      (event) => {
        if (tabsMinX === 0) return;

        const deltaX = normalizeTabsWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setTabsX(tabsCurrentX - deltaX);
      },
      { passive: false }
    );

    tabsViewport?.addEventListener('pointerdown', (event) => {
      if (tabsMinX === 0 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      tabsDrag.pointerId = event.pointerId;
      tabsDrag.startX = event.clientX;
      tabsDrag.startTranslate = tabsCurrentX;
      tabsDrag.moved = false;
      tabsTrack.style.transition = 'none';
      tabsViewport.setPointerCapture?.(event.pointerId);
    });

    tabsViewport?.addEventListener('pointermove', (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - tabsDrag.startX;
      if (!tabsDrag.moved && Math.abs(deltaX) > 5) tabsDrag.moved = true;
      if (!tabsDrag.moved) return;
      event.preventDefault();
      setTabsX(tabsDrag.startTranslate + deltaX);
    });

    const endTabsDrag = (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      tabsViewport?.releasePointerCapture?.(event.pointerId);
      tabsDrag.pointerId = null;

      if (tabsDrag.moved) {
        tabsSuppressClick = true;
        window.setTimeout(() => {
          tabsSuppressClick = false;
        }, 180);
      }

      tabsDrag.moved = false;
      setTabsX(tabsCurrentX);
    };

    tabsViewport?.addEventListener(
      'click',
      (event) => {
        if (!tabsSuppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    tabsViewport?.addEventListener('pointerup', endTabsDrag);
    tabsViewport?.addEventListener('pointercancel', endTabsDrag);
    tabsTrack?.addEventListener('dragstart', (event) => event.preventDefault());

    itemButtons.forEach((item) => {
      item.addEventListener('click', () => {
        const itemIndex = Number.parseInt(item.dataset.gm3TrainIndex || '0', 10);
        if (activeIndex === itemIndex) {
          activeIndex = null;
        } else {
          activeIndex = itemIndex;
          lastMediaIndex = itemIndex;
        }
        setActiveMedia();
        syncVideos();
      });
    });

    root.addEventListener(
      'pointerdown',
      (event) => {
        if (!event.target.closest('[data-gm3-train-video-toggle]')) return;
        event.stopPropagation();
      },
      true
    );

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-gm3-train-video-toggle]');
      if (!toggle || !root.contains(toggle)) return;
      event.preventDefault();
      event.stopPropagation();

      const container = toggle.closest('[data-gm3-train-media], [data-gm3-train-slide]');
      const video = getVideo(container);
      if (!video) return;

      const itemIndex = getElementIndex(container);
      const matchingElements = getElementsByIndex(itemIndex);
      const matchingVideos = matchingElements.map(getVideo).filter(Boolean);

      if (video.paused) {
        pausedIndexes.delete(itemIndex);
        video.muted = true;
        video.playsInline = true;
        video.play().then(updateAllVideoButtons).catch(updateAllVideoButtons);
      } else {
        pausedIndexes.add(itemIndex);
        matchingVideos.forEach((matchingVideo) => matchingVideo.pause());
        updateAllVideoButtons();
      }
    });

    [...mediaItems, ...slides].forEach((element) => {
      const video = getVideo(element);
      if (!video) return;
      video.addEventListener('play', updateAllVideoButtons);
      video.addEventListener('pause', updateAllVideoButtons);
      video.addEventListener('ended', updateAllVideoButtons);
    });

    viewport?.addEventListener('pointerdown', (event) => {
      if (!mediaQuery.matches || visibleSlides.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      drag.pointerId = event.pointerId;
      drag.startX = event.clientX;
      drag.startTranslate = currentX;
      drag.moved = false;
      viewport.setPointerCapture?.(event.pointerId);
      if (track) track.style.transition = 'none';
    });

    viewport?.addEventListener('pointermove', (event) => {
      if (drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(deltaX) > 6) drag.moved = true;
      if (!drag.moved) return;
      setX(drag.startTranslate + deltaX);
      event.preventDefault();
    });

    const endDrag = (event) => {
      if (drag.pointerId !== event.pointerId) return;
      viewport?.releasePointerCapture?.(event.pointerId);
      drag.pointerId = null;

      if (!drag.moved) return;

      const delta = currentX - drag.startTranslate;
      const direction = delta < -20 ? 1 : delta > 20 ? -1 : 0;
      setVisibleSlide(visibleSlideIndex + direction, true);
      drag.moved = false;
    };

    viewport?.addEventListener('pointerup', endDrag);
    viewport?.addEventListener('pointercancel', endDrag);
    track?.addEventListener('dragstart', (event) => event.preventDefault());

    viewport?.addEventListener(
      'wheel',
      (event) => {
        if (!mediaQuery.matches || visibleSlides.length < 2) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setX(currentX - deltaX);
        updateVisibleSlideState();
      },
      { passive: false }
    );

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        scheduleTabsRefresh();
        refreshBounds();
        setVisibleSlide(visibleSlideIndex, false);
      }, 120);
    });

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', () => {
        refreshSlides();
        syncVideos();
      });
    }

    setTab(activeTab);
    updateAllVideoButtons();
    scheduleTabsRefresh();
    window.addEventListener('load', scheduleTabsRefresh, { once: true });
    document.fonts?.ready?.then(scheduleTabsRefresh).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-train]').forEach(initTrain);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
